import AsyncStorage from '@react-native-async-storage/async-storage';
import { productProfile } from '../productProfile';

export const REMUX_SERVER_URL = productProfile.presetServerUrl || 'https://remux.obnoxious.lol';
const CREDENTIALS_KEY = 'jellyflix.credentials.v1';
const DEVICE_ID_KEY = 'jellyflix.device-id.v1';

export type Credentials = { serverUrl: string; userId: string; accessToken: string; deviceId: string };

export function normalizeServerUrl(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) throw new Error('Enter your Remux server URL.');
    const withScheme = /^[a-z][a-z\d+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    if (!/^https?:\/\//i.test(withScheme)) throw new Error('Remux server URLs must use HTTP or HTTPS.');

    // React Native's built-in URL class does not implement hostname/hash/search.
    // Validate the authority directly so native login does not depend on the
    // browser URL implementation.
    const match = /^(https?):\/\/([^/?#]+)(?:\/[^?#]*)?(?:[?#].*)?$/i.exec(withScheme);
    if (!match) throw new Error('Enter a valid Remux server URL.');

    const authority = match[2];
    const hostPort = authority.slice(authority.lastIndexOf('@') + 1);
    let hostname = hostPort;
    if (hostPort.startsWith('[')) {
        const closingBracket = hostPort.indexOf(']');
        const suffix = closingBracket >= 0 ? hostPort.slice(closingBracket + 1) : '';
        if (closingBracket < 2 || (suffix && !/^:\d+$/.test(suffix))) {
            throw new Error('Enter a valid Remux server URL.');
        }
        hostname = hostPort.slice(1, closingBracket);
    } else if (hostPort.includes(':')) {
        const separator = hostPort.lastIndexOf(':');
        hostname = hostPort.slice(0, separator);
        if (!hostname || !/^:\d+$/.test(hostPort.slice(separator))) {
            throw new Error('Enter a valid Remux server URL.');
        }
    }
    if (!hostname || /[\s@]/.test(hostname)) throw new Error('Enter a valid Remux server URL.');

    return withScheme.split(/[?#]/, 1)[0].replace(/\/+$/, '');
}

export async function getDeviceId(): Promise<string> {
    const existing = await AsyncStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const created = `rnv-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    await AsyncStorage.setItem(DEVICE_ID_KEY, created);
    return created;
}

export async function readCredentials(): Promise<Credentials | null> {
    const raw = await AsyncStorage.getItem(CREDENTIALS_KEY);
    if (!raw) return null;
    try {
        const value = JSON.parse(raw) as Partial<Credentials>;
        if (!value.userId?.trim() || !value.accessToken?.trim()) return null;
        const serverUrl = productProfile.presetServerUrl
            ? REMUX_SERVER_URL
            : value.serverUrl
                ? normalizeServerUrl(value.serverUrl)
                : '';
        if (!serverUrl) return null;
        return { serverUrl, userId: value.userId, accessToken: value.accessToken, deviceId: value.deviceId ?? await getDeviceId() };
    } catch {
        await AsyncStorage.removeItem(CREDENTIALS_KEY);
        return null;
    }
}

export async function saveCredentials(value: Credentials): Promise<void> {
    if (!value.userId.trim() || !value.accessToken.trim()) throw new Error('Cannot save incomplete Jellyfin credentials.');
    const serverUrl = productProfile.presetServerUrl ? REMUX_SERVER_URL : normalizeServerUrl(value.serverUrl);
    await AsyncStorage.setItem(CREDENTIALS_KEY, JSON.stringify({ ...value, serverUrl }));
}

export async function clearCredentials(): Promise<void> {
    await AsyncStorage.removeItem(CREDENTIALS_KEY);
}
